import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { fmt } from 'pages/VocabularyTest/cefr';
import { STATUS_META } from 'components/dictionaryView/statusMeta';

interface CategoryDefinition {
  key: 'learned' | 'repeat' | 'active';
  label: string;
  desc: string;
  color: string;
  count: number;
}

interface TestResultCategoryStatsProps {
  learnedCount: number;
  repeatCount: number;
  activeCount: number;
}

const TestResultCategoryStats: React.FC<TestResultCategoryStatsProps> = ({
  learnedCount,
  repeatCount,
  activeCount,
}) => {
  const total = learnedCount + repeatCount + activeCount;
  const categories: CategoryDefinition[] = [
    {
      key: 'learned',
      label: 'Вивчено',
      desc: 'Знаєш упевнено — пасивний запас',
      color: 'primary.vocabularyLearned',
      count: learnedCount,
    },
    {
      key: 'repeat',
      label: STATUS_META.repeat.label,
      desc: STATUS_META.repeat.desc,
      color: STATUS_META.repeat.color,
      count: repeatCount,
    },
    {
      key: 'active',
      label: 'Активне мовлення',
      desc: 'Вживаєш вільно — активне мовлення',
      color: 'primary.vocabularyActiveSpeech',
      count: activeCount,
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        p: 3,
      }}
    >
      <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>
        {'Слова за категоріями'}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 2.5 }}>
        {`Як розподілилися ${fmt(total)} перевірених слів`}
      </Typography>

      <Stack
        direction={'row'}
        sx={{
          height: 18,
          borderRadius: 999,
          overflow: 'hidden',
          bgcolor: 'primary.tableHeadBg',
          mb: 2.5,
        }}
      >
        {categories.map((c) =>
          total > 0 && c.count > 0 ? (
            <Box
              key={c.key}
              sx={{ width: `${(c.count / total) * 100}%`, bgcolor: c.color }}
            />
          ) : null,
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2.25,
        }}
      >
        {categories.map((c) => (
          <Box
            key={c.key}
            sx={{
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              borderRadius: 2,
              p: '18px 20px',
            }}
          >
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  bgcolor: c.color,
                }}
              />
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                {c.label}
              </Typography>
            </Stack>
            <Stack
              direction={'row'}
              alignItems={'baseline'}
              spacing={1}
              sx={{ mt: 1.25 }}
            >
              <Typography sx={{ fontSize: 38, fontWeight: 800, lineHeight: 1 }}>
                {fmt(c.count)}
              </Typography>
              <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>
                {`слів · ${total > 0 ? Math.round((c.count / total) * 100) : 0}%`}
              </Typography>
            </Stack>
            <Typography sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>
              {c.desc}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TestResultCategoryStats;
